/**
 * DashboardPage — Command Dashboard embedded directly as a React component.
 * JS/CSS/HTML sourced from public/dashboard.html — kept in sync manually.
 * Reads from localStorage (same origin as the rest of the app).
 */
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

const CSS = `
* { box-sizing: border-box; margin: 0; padding: 0; }
:root {
  --bg:     #060d18;
  --panel:  #0a1420;
  --card:   #0d1a28;
  --border: rgba(255,255,255,0.06);
  --border2: rgba(255,255,255,0.10);
  --text:   #e2e8f0;
  --muted:  #2a4a6e;
  --subtle: #5a7a9a;
  --dim:    #7a9ab8;
  --accent: #b8862a;
  --accent-glow: rgba(184,134,42,0.18);
  --green:  #2ecc71;
  --amber:  #e67e22;
  --red:    #c0392b;
  --blue:   #3b82f6;
  --purple: #9b59b6;
}

/* ── PAGE FRAME: locked to viewport, NO outer page scroll ──
   Total layout fits within the VISIBLE viewport. 100dvh (dynamic viewport
   height) tracks the real visible area on tablets where the browser bar
   grows/shrinks — plain 100vh measured the largest viewport and clipped
   the bottom row on tablets. 100vh line kept as fallback for old engines. */
html, body {
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  background: var(--bg);
  color: var(--text);
  font-family: 'Segoe UI', system-ui, sans-serif;
  font-size: 14px;
  font-variant-numeric: tabular-nums;
}

/* ── INVISIBLE-UNTIL-SCROLL SCROLLBARS ──
   The thumb is transparent at rest. When the user starts scrolling
   (or hovers a scrollable area), the thumb fades in. Track is always
   transparent so the page background shows through. */
.scrollable {
  scrollbar-width: thin;
  scrollbar-color: transparent transparent;
}
.scrollable:hover, .scrollable:focus-within, .scrollable.is-scrolling {
  scrollbar-color: rgba(255,255,255,0.18) transparent;
}
.scrollable::-webkit-scrollbar { width: 6px; height: 6px; }
.scrollable::-webkit-scrollbar-track { background: transparent; }
.scrollable::-webkit-scrollbar-thumb {
  background: transparent;
  border-radius: 3px;
  transition: background 0.2s ease;
}
.scrollable:hover::-webkit-scrollbar-thumb,
.scrollable:focus-within::-webkit-scrollbar-thumb,
.scrollable.is-scrolling::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.18);
}

/* ── TOPBAR ── */
#topbar {
  height: 44px; background: var(--panel);
  border-bottom: 1px solid var(--border);
  display: flex; align-items: center; padding: 0 18px; gap: 16px;
  flex-shrink: 0;
}
#topbar .logo {
  font-size: 14px; font-weight: 700; color: var(--text);
  letter-spacing: .12em; text-transform: uppercase;
}
#topbar .logo span { color: var(--accent); }
.tb-div { width: 1px; height: 18px; background: var(--border2); }
.tb-item { font-size: 12px; color: var(--subtle); white-space: nowrap; }
.tb-item b { color: #b0c4d8; font-weight: 600; }
#tb-clock { font-size: 17px; font-weight: 600; color: var(--accent); letter-spacing: .06em; }
.tb-right { margin-left: auto; display: flex; align-items: center; gap: 12px; }
#tb-back {
  font-size: 10px; font-weight: 500; color: var(--subtle);
  text-decoration: none; padding: 3px 10px;
  border: 1px solid var(--border2); border-radius: 4px;
  transition: color .15s;
}
#tb-back:hover { color: var(--text); }
#tb-refresh {
  font-size: 10px; font-weight: 500; color: var(--accent);
  background: none; border: 1px solid rgba(184,134,42,0.25);
  border-radius: 4px; padding: 3px 10px; cursor: pointer;
}
#tb-refresh:hover { background: rgba(184,134,42,0.08); }

/* ── CONTENT ROW: holds #main and the Incidents sidebar side by side,
   spanning the full height below the topbar. ── */
#content-row {
  display: flex;
  height: calc(100vh - 44px);
  height: calc(100dvh - 44px);
  overflow: hidden;
}

/* ── MAIN: column flexbox that fills remaining vertical space ── */
#main {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-width: 0;
  height: 100%;
  overflow: hidden;
}

/* ── INCIDENTS SIDEBAR — retractable, spans full height beside #main.
   Collapsed = thin vertical tab; expanded = panel that pushes #main
   narrower (no overlay). State persists via localStorage. ── */
#incidents-sidebar {
  flex-shrink: 0;
  height: 100%;
  display: flex;
  border-left: 1px solid var(--border);
  background: var(--panel);
  transition: width 0.18s ease;
  overflow: hidden;
  width: 300px;
}
#incidents-sidebar.collapsed { width: 34px; }
#incidents-collapsed-tab {
  display: none;
  width: 34px; flex-shrink: 0;
  flex-direction: column; align-items: center; justify-content: center;
  gap: 12px; cursor: pointer;
  background: var(--panel);
  transition: background 0.15s;
}
#incidents-collapsed-tab:hover { background: rgba(184,134,42,0.07); }
#incidents-sidebar.collapsed #incidents-collapsed-tab { display: flex; }
#incidents-sidebar.collapsed #incidents-expanded { display: none; }
#incidents-collapsed-tab .tab-arrow {
  font-size: 18px; color: var(--accent); line-height: 1; user-select: none;
}
#incidents-collapsed-tab .rot {
  writing-mode: vertical-rl; text-orientation: mixed;
  font-size: 10px; font-weight: 700; letter-spacing: .1em;
  color: var(--subtle); text-transform: uppercase;
}
#incidents-tab-count {
  display: none;
  background: var(--amber); color: #1a1208;
  font-size: 10px; font-weight: 700; border-radius: 10px; padding: 1px 5px;
  line-height: 1.4;
}
#incidents-expanded {
  display: flex; flex-direction: column;
  flex: 1 1 auto; min-width: 0; min-height: 0;
  padding: 10px 12px;
}
#incidents-expanded .d-card-title { flex-shrink: 0; }
#incidents-collapse-btn {
  background: none; border: none; border-radius: 4px;
  color: var(--accent); cursor: pointer; font-size: 16px;
  padding: 0 4px; line-height: 1;
  transition: color 0.15s;
}
#incidents-collapse-btn:hover { color: var(--text); }
#incidents-expanded #incident-list { flex: 1 1 auto; min-height: 0; overflow-y: auto; }

/* ── STAT ROW ── */
#stat-row {
  display: grid; grid-template-columns: repeat(8,1fr);
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}
.stat-cell {
  padding: 16px 12px; border-right: 1px solid var(--border);
  display: flex; flex-direction: column; gap: 6px; align-items: center; text-align: center;
}
.stat-cell:last-child { border-right: none; }
.stat-label {
  font-size: 11px; font-weight: 600; color: var(--subtle);
  text-transform: uppercase; letter-spacing: .07em;
}
.stat-val {
  font-size: 36px; font-weight: 700; color: var(--text);
  line-height: 1;
}
.stat-val.alert  { color: var(--red); }
.stat-val.warn   { color: var(--amber); }
.stat-val.ok     { color: var(--green); }

/* ── INFO BAR ──
   The "Shift" cell gets a subtle gold-tinted background to mark
   the active shift visually. Replaces the redundant topbar shift label. */
#info-bar {
  display: grid; grid-template-columns: repeat(5,1fr);
  border-bottom: 1px solid var(--border);
  flex-shrink: 0;
}
.info-cell {
  padding: 6px 12px; border-right: 1px solid var(--border);
  position: relative;
}
.info-cell:last-child { border-right: none; }
.info-label {
  font-size: 11px; font-weight: 600; color: var(--subtle);
  text-transform: uppercase; letter-spacing: .07em;
}
.info-val { font-size: 13px; font-weight: 500; color: #b0c4d8; margin-top: 2px; }
/* Active shift highlighting — applied to the Shift and Shift Time cells */
.info-cell.active-shift {
  background: linear-gradient(180deg, var(--accent-glow) 0%, transparent 100%);
}
.info-cell.active-shift .info-label { color: var(--accent); }
.info-cell.active-shift .info-val   { color: var(--accent); font-weight: 600; }

/* ── BODY: takes remaining vertical space, internal scroll only ── */
#body {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

/* ── ROW LAYOUT ──
   Three rows split the body's vertical space. Row 1 (roster + tasks) gets
   the largest share since it has the most content; rows 2 & 3 (equipment
   grids) are roughly equal and don't scroll because their content is
   stable per shift. */
.dash-row {
  display: grid;
  border-bottom: 1px solid var(--border);
  min-height: 0;
}
.r-3   { grid-template-columns: 2fr 1fr 1fr; flex: 1.6 1 0; }  /* Row 1 — biggest (Roster wider | Primary | Secondary). Incidents lives in the sidebar now. */
.r-3b  { grid-template-columns: 1fr 1fr 1fr; flex: 0 0 auto; height: auto; }
.r-3c  { grid-template-columns: 1fr 1fr 0.5fr 0.5fr; flex: 0 0 auto; height: auto; }
/* Equipment rows: cards and content shrink to fit */
.r-3b .d-card,
.r-3c .d-card { height: auto; min-height: 0; overflow: visible; }
.r-3b .d-card-content,
.r-3c .d-card-content { flex: 0 0 auto; overflow: visible; min-height: 0; }
.r-3b .eq-grid,
.r-3c .eq-grid { height: auto; }
.r-2   { grid-template-columns: 1fr 1fr; flex: 1 1 0; }
#body .dash-row:last-of-type { border-bottom: none; }

/* ── CARD ── */
.d-card {
  padding: 8px 12px;
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}
.d-card:last-child { border-right: none; }
.d-card-title {
  font-size: 11px; font-weight: 600; color: var(--subtle);
  text-transform: uppercase; letter-spacing: .08em;
  padding-bottom: 6px; margin-bottom: 8px;
  border-bottom: 1px solid var(--border2);
  display: flex; align-items: center; justify-content: space-between;
  flex-shrink: 0;
}
.d-card-sub { font-size: 11px; font-weight: 400; color: var(--dim); text-transform: none; letter-spacing: 0; }

/* ── ROW 1 SCROLLABLE CONTENT AREAS ──
   The card content fills the available card height. When content
   overflows, the inner area scrolls; the card itself never grows
   beyond its grid cell. This keeps the whole dashboard at 100vh. */
.r-3 .d-card-content {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
}

/* ── OFFICER CARDS ──
   Border color is driven by current post assignment, not just role.
   Supervisors keep purple regardless to preserve rank-at-a-glance. */
#roster-wrap { display: flex; flex-direction: column; gap: 4px; padding-right: 4px; }
.officer-card {
  border-left: 3px solid var(--muted);
  padding: 6px 10px;
  background: rgba(255,255,255,0.015);
  border-radius: 0 4px 4px 0;
  display: flex; flex-direction: row; align-items: center; gap: 8px;
}
/* Role-driven (supervisors stay purple regardless of post) */
.officer-card.is-sup        { border-left-color: var(--purple); }
/* Post-driven coloring for officers — applied when officer has a current post */
.officer-card.post-mall     { border-left-color: var(--blue); }
.officer-card.post-vehicle  { border-left-color: var(--amber); }
.officer-card.post-desk     { border-left-color: var(--green); }
.officer-card.post-special  { border-left-color: var(--purple); }
.officer-card.post-break    { border-left-color: var(--amber); background: rgba(230,126,34,0.05); }
.officer-card.idle          { border-left-color: var(--muted); }
.oc-name-row { display: flex; align-items: baseline; gap: 6px; }
.oc-left { flex: 1; display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.oc-badge-img { width: 40px; height: auto; flex-shrink: 0; opacity: 0.9; }
.oc-rank  { font-size: 9px; font-weight: 600; color: var(--dim); text-transform: uppercase; letter-spacing: .05em; }
.oc-name  { font-size: 13px; font-weight: 600; color: #b0c4d8; }
.oc-name.sup { color: #b07ad8; }
.oc-cards { display: flex; gap: 4px; flex-wrap: wrap; margin-top: 2px; }
.oc-sub {
  background: rgba(0,0,0,0.3); border: 1px solid var(--border2);
  border-radius: 4px; padding: 2px 7px;
  display: flex; align-items: center; gap: 5px; white-space: nowrap;
}
/* The "now" item — the assignment currently active — gets a brighter background */
.oc-sub.is-now {
  background: rgba(184,134,42,0.12);
  border-color: rgba(184,134,42,0.40);
}
.oc-sub-val   { font-size: 11px; color: var(--dim); }
.oc-sub.is-now .oc-sub-val { color: var(--accent); font-weight: 600; }
.oc-sub-val.break { color: var(--amber); }
.oc-sub-val.post  { color: #94b8d8; }

/* ── TASK CARDS ── */
.task-list { display: flex; flex-direction: column; gap: 4px; padding-right: 4px; }
.task-item {
  border-left: 2px solid var(--muted);
  padding: 5px 8px;
  background: rgba(255,255,255,0.015);
  border-radius: 0 3px 3px 0;
}
.task-item.active { border-left-color: var(--green); }
.task-name  { font-size: 13px; font-weight: 500; color: #b0c4d8; }
.task-who   { font-size: 11px; color: var(--dim); margin-top: 3px; }

/* ── EQUIPMENT GRID ──
   Rows 2 & 3 grids never scroll internally — they fit what they fit. */
.eq-grid {
  display: grid;
  gap: 4px;
  padding: 2px;
  align-content: start;
}
.eq-card {
  border: 1px solid var(--border2); border-left: 3px solid var(--border2); border-radius: 4px;
  padding: 6px 8px; text-align: center; background: var(--panel);
  min-width: 52px;
}
.eq-card .eq-id { font-size: 14px; font-weight: 700; color: #c8dff0; line-height: 1; }
.eq-card .eq-st { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; margin-top: 3px; }
.eq-card .eq-who { font-size: 10px; color: var(--dim); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.eq-card.avail { border-color: #1a4731; border-left-color: #1a6b40; background: rgba(26,71,49,0.12); }
.eq-card.avail .eq-st { color: var(--green); }
.eq-card.out   { border-color: #1a3a5c; border-left-color: #185FA5; background: rgba(59,130,246,0.12); }
.eq-card.out   .eq-st { color: #3b82f6; }
.eq-card.oos   { border-color: #4a0d0d; border-left-color: #7a1f1f; background: rgba(74,13,13,0.2); opacity: .55; }
.eq-card.oos   .eq-st { color: var(--red); }

/* Equipment rows — no stretching, fit content only */
.r-3b, .r-3c {
  flex: 0 0 auto !important;
  height: auto !important;
  min-height: 0 !important;
}
.r-3b .d-card,
.r-3c .d-card {
  height: auto !important;
  min-height: 0 !important;
  flex: 1 1 0;
}
.r-3b .eq-grid,
.r-3c .eq-grid {
  align-content: start;
}

/* ── LIST ROWS (Access & Other Items) ── */
.list-row {
  display: flex; align-items: center; gap: 6px;
  padding: 5px 0; border-bottom: 1px solid var(--border);
  font-size: 12px;
}
.list-row:last-child { border-bottom: none; }
.list-row .lf { flex: 1; color: var(--dim); }
.list-row .lv { color: #b0c4d8; font-weight: 500; }
.ldot {
  width: 6px; height: 6px; border-radius: 50%;
  display: inline-block; flex-shrink: 0;
}
.chip {
  font-size: 11px; font-weight: 600; padding: 2px 8px;
  border-radius: 3px; white-space: nowrap;
}
.no-data { font-size: 12px; color: var(--dim); padding: 6px 0; }

.break-list { display: flex; flex-direction: column; gap: 4px; }
.break-row { display: flex; align-items: center; gap: 8px; padding: 4px 8px; background: rgba(230,126,34,0.07); border: 1px solid rgba(230,126,34,0.18); border-radius: 4px; }
.break-time { font-size: 12px; font-weight: 700; color: var(--amber); min-width: 52px; }
.break-who  { font-size: 12px; color: #94b8d8; flex: 1; }
.break-cov  { font-size: 11px; color: var(--subtle); }
.notes-body p { margin: 0 0 4px; font-size: 12px; color: var(--dim); line-height: 1.6; }

/* ── OOS BAR ──
   Shows beneath rows when items are out of service. Pinned to bottom. */
#oos-bar {
  display: flex;
  align-items: center;
  padding: 5px 12px;
  border-top: 1px solid var(--border);
  flex-shrink: 0;
  min-height: 28px;
  transition: background 0.2s, border-color 0.2s;
}
.oos-label {
  font-size: 11px; font-weight: 700; text-transform: uppercase;
  letter-spacing: .08em; color: var(--red); white-space: nowrap;
}
#oos-chips { display: flex; flex-wrap: wrap; gap: 5px; }

/* ── LAST UPDATED (inside OOS bar) ── */
#last-updated {
  font-size: 10px; color: var(--subtle);
}

/* ── BADGE STRIP ──
   One row spanning the dashboard, badges spread evenly end-to-end. On narrow
   screens it wraps; badges shrink at the mobile breakpoint to keep one row.
   off = greyed (off duty/unassigned); on = lit + last name; oos = grey+red. */
#badge-strip {
  display: flex; flex-wrap: wrap;
  justify-content: space-between;
  row-gap: 2px;
  padding: 2px 12px;
  border-top: 1px solid var(--border);
  flex-shrink: 0; align-items: center;
}
.bstrip-badge {
  width: 60px; flex-shrink: 0;
  display: flex; flex-direction: column; align-items: center; gap: 0;
  padding: 0 1px; border-radius: 4px; cursor: default;
}
.bstrip-badge.clickable { cursor: pointer; }
.bstrip-badge .bb-img-wrap { position: relative; width: 52px; height: auto; }
.bstrip-badge img { display: block; width: 100%; height: auto; }
.bstrip-badge .bb-num {
  position: absolute; top: 46%; left: 50%; transform: translate(-50%,-50%);
  font-weight: 900; font-size: 13px; font-family: 'Arial Black', sans-serif;
  line-height: 1; pointer-events: none;
}
.bstrip-badge .bb-name {
  font-size: 8px; color: var(--dim); max-width: 40px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap; text-align: center;
}
.bstrip-badge.off { opacity: 0.3; filter: grayscale(1); }
.bstrip-badge.on  { opacity: 1; }
.bstrip-badge.oos {
  opacity: 0.5; filter: grayscale(0.7);
  background: rgba(192,57,43,0.35); border: 1px solid rgba(192,57,43,0.6);
}
@media (max-width: 768px) {
  #badge-strip { padding: 4px 8px; row-gap: 4px; }
  .bstrip-badge { width: 48px; }
  .bstrip-badge .bb-img-wrap { width: 42px; }
}

/* ── MOBILE & TABLET RESPONSIVE ──
   Mobile inverts the locked-viewport rule because content can't fit.
   On phones, the page DOES scroll naturally as a single column. */
@media (max-width: 768px) {
  html, body { height: auto; overflow: auto; -webkit-text-size-adjust: 100%; }

  #topbar {
    height: auto; min-height: 44px;
    padding: 6px 10px; gap: 8px;
    flex-wrap: wrap; position: sticky; top: 0; z-index: 50;
  }
  #topbar .logo { font-size: 12px; }
  #topbar .tb-div { display: none; }
  #topbar .tb-item { font-size: 11px; }
  #tb-weather { display: none; }
  #tb-clock { font-size: 14px; }
  .tb-right { gap: 8px; flex-wrap: wrap; margin-left: auto; }

  #main { height: auto; overflow: visible; }

  #stat-row { grid-template-columns: repeat(4, 1fr); }
  .stat-cell { padding: 8px 6px; border-bottom: 1px solid var(--border); }
  .stat-cell:nth-child(4n) { border-right: none; }
  .stat-cell:nth-last-child(-n+4) { border-bottom: none; }
  .stat-cell .stat-label { font-size: 10px; }
  .stat-cell .stat-val { font-size: 22px; }

  #info-bar { grid-template-columns: repeat(2, 1fr); }
  .info-cell { padding: 5px 8px; border-bottom: 1px solid var(--border); }
  .info-cell:nth-child(2n) { border-right: none; }
  .info-cell:nth-child(5) { grid-column: 1 / -1; border-right: none; border-bottom: none; }
  .info-cell:nth-child(3), .info-cell:nth-child(4) { border-bottom: 1px solid var(--border); }

  #body { display: block; flex: none; height: auto; overflow: visible; }
  .dash-row.r-3,
  .dash-row.r-3b,
  .dash-row.r-2 { grid-template-columns: 1fr; flex: none; }
  .d-card {
    border-right: none;
    border-bottom: 1px solid var(--border);
    height: auto !important;
    overflow: visible;
  }
  .dash-row .d-card:last-child { border-bottom: none; }
  .r-3 .d-card-content { overflow: visible; }

  .officer-card { padding: 6px 8px; }
  .oc-name { font-size: 12px; }

  .eq-grid {
    grid-template-columns: repeat(3, 1fr) !important;
  }

  /* Sidebar concept doesn't fit a single-column phone layout — render
     Incidents as a normal full-width stacked section instead. */
  #content-row { display: block; height: auto; overflow: visible; }
  #incidents-sidebar {
    width: 100% !important; height: auto;
    border-left: none; border-top: 1px solid var(--border);
  }
  #incidents-collapsed-tab { display: none !important; }
  #incidents-expanded { display: flex !important; }
  #incidents-expanded #incident-list { max-height: 240px; }
}

@media (max-width: 480px) {
  #stat-row { grid-template-columns: repeat(2, 1fr); }
  .stat-cell { border-right: 1px solid var(--border); border-bottom: 1px solid var(--border); }
  .stat-cell:nth-child(2n) { border-right: none; }
  .stat-cell:nth-last-child(-n+2) { border-bottom: none; }
  .stat-cell:nth-last-child(-n+4) { border-bottom: 1px solid var(--border); }
  .stat-cell:nth-last-child(-n+2) { border-bottom: none; }

  #info-bar { grid-template-columns: 1fr; }
  .info-cell { border-right: none; border-bottom: 1px solid var(--border); }
  .info-cell:last-child { border-bottom: none; }
  .info-cell:nth-child(5) { grid-column: auto; }
  .info-cell:nth-child(3), .info-cell:nth-child(4) { border-bottom: 1px solid var(--border); }

  .eq-grid { grid-template-columns: repeat(2, 1fr) !important; }

  #topbar { padding: 4px 8px; }
  #topbar .logo { font-size: 11px; letter-spacing: .08em; }
  #tb-date { display: none; }
  #tb-back { font-size: 9px; padding: 2px 6px; }
  #tb-refresh { font-size: 9px; padding: 2px 6px; }
}
`

const HTML = `
<!-- TOPBAR — shift label removed (info bar already shows it) -->
<div id="topbar">
  <div class="logo">FOM <span>/</span> Command</div>
  <div class="tb-div"></div>
  <div class="tb-item" id="tb-date"></div>
  <div class="tb-div"></div>
  <div class="tb-item" id="tb-weather"></div>
  <div class="tb-right">
    <div id="tb-clock">--:--</div>
    <div class="tb-div"></div>
    <button id="tb-refresh" onclick="render()">&#x21BA; Refresh</button>
    <a href="/" id="tb-back">&#x2190; Back</a>
  </div>
</div>

<div id="content-row">
<div id="main">

  <!-- STAT ROW -->
  <div id="stat-row">
    <div class="stat-cell"><div class="stat-label">On Duty</div><div class="stat-val ok" id="m-duty">0</div></div>
    <div class="stat-cell"><div class="stat-label">On Post</div><div class="stat-val" id="m-post">0</div></div>
    <div class="stat-cell"><div class="stat-label">Incidents</div><div class="stat-val" id="m-incidents">0</div></div>
    <div class="stat-cell"><div class="stat-label">Radios Out</div><div class="stat-val" id="m-rout">0</div></div>
    <div class="stat-cell"><div class="stat-label">Keys Out</div><div class="stat-val" id="m-kout">0</div></div>
    <div class="stat-cell"><div class="stat-label">Recorders</div><div class="stat-val" id="m-recout">0</div></div>
    <div class="stat-cell"><div class="stat-label">Badges OOS</div><div class="stat-val" id="m-boos">0</div></div>
    <div class="stat-cell"><div class="stat-label">Total OOS</div><div class="stat-val" id="m-toos">0</div></div>
  </div>

  <!-- INFO BAR — Shift Time and Shift cells are accent-highlighted as the active shift -->
  <div id="info-bar">
    <div class="info-cell"><div class="info-label">Supervisor</div><div class="info-val" id="ib-sup">&mdash;</div></div>
    <div class="info-cell"><div class="info-label">Desk Officer</div><div class="info-val" id="ib-desk">&mdash;</div></div>
    <div class="info-cell active-shift"><div class="info-label">Shift Time</div><div class="info-val" id="ib-stime">&mdash;</div></div>
    <div class="info-cell active-shift"><div class="info-label">Shift</div><div class="info-val" id="ib-shift">&mdash;</div></div>
    <div class="info-cell"><div class="info-label">Shift Ends</div><div class="info-val" id="ib-countdown">&mdash;</div></div>
  </div>

  <div id="body">

    <!-- ROW 1: Roster | Primary Tasks | Secondary Tasks
         Each card's content area scrolls internally when overflowing. -->
    <div class="dash-row r-3">
      <div class="d-card">
        <div class="d-card-title">Officer Roster <span class="d-card-sub" id="roster-sub"></span></div>
        <div class="d-card-content scrollable" id="roster-wrap-outer">
          <div id="roster-wrap"></div>
        </div>
      </div>
      <div class="d-card">
        <div class="d-card-title">Primary Tasks</div>
        <div class="d-card-content scrollable" id="primary-task-area"></div>
      </div>
      <div class="d-card">
        <div class="d-card-title">Secondary Tasks <span class="d-card-sub" id="sec-task-sub"></span></div>
        <div class="d-card-content scrollable">
          <div class="task-list" id="tasks-list"></div>
        </div>
      </div>
    </div>

    <!-- ROW 2: Sup Radios | Officer Radios | Keys -->
    <div class="dash-row r-3b">
      <div class="d-card">
        <div class="d-card-title">Supervisor Radios <span class="d-card-sub" id="sup-radio-sub"></span></div>
        <div class="eq-grid" id="sup-radio-grid"></div>
      </div>
      <div class="d-card">
        <div class="d-card-title">Officer Radios <span class="d-card-sub" id="off-radio-sub"></span></div>
        <div class="eq-grid" id="off-radio-grid"></div>
      </div>
      <div class="d-card">
        <div class="d-card-title">Security Keys <span class="d-card-sub" id="key-sub"></span></div>
        <div class="eq-grid" id="key-grid"></div>
      </div>
    </div>

    <!-- ROW 3: Recorders | Other Radios | Access -->
    <div class="dash-row r-3c">
      <div class="d-card">
        <div class="d-card-title">Recorders <span class="d-card-sub" id="rec-sub"></span></div>
        <div class="eq-grid" id="rec-grid"></div>
      </div>
      <div class="d-card">
        <div class="d-card-title">Management &amp; Other Radios <span class="d-card-sub" id="other-radio-sub"></span></div>
        <div class="eq-grid" id="other-radio-grid"></div>
      </div>
      <div class="d-card">
        <div class="d-card-title">Break Schedule <span class="d-card-sub" id="break-sub"></span></div>
        <div class="break-list d-card-content scrollable" id="break-list"><div class="no-data">No breaks scheduled.</div></div>
      </div>
      <div class="d-card">
        <div class="d-card-title">Access &amp; Other Items</div>
        <div id="other-items-list"></div>
      </div>
    </div>

  </div>

  <!-- BADGE STRIP — all badges (supervisor + officer) across the dashboard.
       Off duty/unassigned = greyed; on duty = lit + last name; OOS = grey+red.
       Click toggles OOS (supervisor+ only). Rendered by dashboard-logic.js. -->
  <div id="badge-strip"></div>

  <!-- OOS BAR + LAST UPDATED — always visible at bottom -->
  <div id="oos-bar">
    <div id="oos-content" style="display:none;align-items:center;gap:10px;flex:1">
      <span class="oos-label" id="oos-title">&#x26A0; Out of Service</span>
      <div id="oos-chips"></div>
    </div>
    <div id="last-updated" style="margin-left:auto"></div>
  </div>
</div>

<!-- INCIDENTS SIDEBAR — retractable, full-height. Click either the
     collapsed tab or the collapse button to toggle; toggleIncidentsSidebar()
     in dashboard-logic.js persists the state to localStorage. -->
<div id="incidents-sidebar" class="collapsed">
  <div id="incidents-collapsed-tab" onclick="toggleIncidentsSidebar()">
    <span class="tab-arrow">&#x2039;</span>
    <span class="rot">Incidents</span>
    <span id="incidents-tab-count"></span>
  </div>
  <div id="incidents-expanded">
    <div class="d-card-title">
      Incidents (Gatehouse) <span class="d-card-sub" id="incident-sub"></span>
      <button id="incidents-collapse-btn" onclick="toggleIncidentsSidebar()">&#x203A;</button>
    </div>
    <div class="scrollable" id="incident-list"><div class="no-data">Loading…</div></div>
  </div>
</div>
</div>
`

const JS_SRC = `
var SUP_ROLES = ['Dir/AD/Lt','Sergeant','OIC','Lieutenant','Director of Security','Assistant Director','Director','Developer'];
var PRIMARY_POSTS = ['Mall','Vehicle','Desk','Special'];
var POST_COLORS = {Mall:'var(--blue)',Vehicle:'var(--amber)',Desk:'var(--green)',Special:'var(--purple)'};
var POST_CLASS = {Mall:'post-mall',Vehicle:'post-vehicle',Desk:'post-desk',Special:'post-special'};

function ls(k,d){try{var v=localStorage.getItem(k);return v?JSON.parse(v):d;}catch(e){return d;}}
function raw(k){
  var v=localStorage.getItem(k)||'';
  if(v.length>=2&&v.charAt(0)==='"'&&v.charAt(v.length-1)==='"'){
    try{return JSON.parse(v);}catch(e){}
  }
  return v;
}

function sn(n){
  if(!n)return'\u2014';
  n=n.replace(/^(Dir\.|AD\.|Lt\.|Sgt\.|Ofc\.)\s*/i,'').replace(/\s*\([^)]*\)\s*/g,' ').trim();
  if(n.indexOf(',')>=0)return n.split(',')[0].trim();
  var p=n.split(/\s+/);return p.length>1?p[p.length-1]:p[0];
}
function nowET(){
  try{return new Date().toLocaleString('en-US',{timeZone:'America/New_York',hour:'2-digit',minute:'2-digit',hour12:false});}
  catch(e){return new Date().toTimeString().slice(0,5);}
}
function shiftTime(s){
  return s==='Morning'?'07:00\u201315:00':s==='Evening'?'15:00\u201323:00':s==='Night'?'23:00\u201307:00':s||'\u2014';
}
function currentShift(){
  try{
    var h=parseInt(new Date().toLocaleString('en-US',{timeZone:'America/New_York',hour:'2-digit',hour12:false}).split(':')[0]);
    return h>=7&&h<15?'Morning':h>=15&&h<23?'Evening':'Night';
  }catch(e){
    var d=new Date(),h=d.getHours();
    return h>=7&&h<15?'Morning':h>=15&&h<23?'Evening':'Night';
  }
}
function nowMins(){
  try{var s=new Date().toLocaleString('en-US',{timeZone:'America/New_York',hour:'2-digit',minute:'2-digit',hour12:false});var p=s.split(':');return parseInt(p[0])*60+parseInt(p[1]);}
  catch(e){var d=new Date();return d.getHours()*60+d.getMinutes();}
}
function parseMins(t){
  if(!t)return null;t=t.trim();
  if(/^\d{4}$/.test(t))return parseInt(t.slice(0,2))*60+parseInt(t.slice(2));
  if(/^\d{1,2}:\d{2}$/.test(t)){var p=t.split(':');return parseInt(p[0])*60+parseInt(p[1]);}
  return null;
}
function isActive(from,to){
  if(!from&&!to)return true;
  var now=nowMins(),f=parseMins(from),t=parseMins(to);
  if(f===null||t===null)return true;
  if(t<=f)t+=1440;
  var n=now<f?now+1440:now;
  return n>=f&&n<t;
}
function postSortKey(p){
  var f=parseMins(p.from);
  return f===null?99999:f;
}
function normCS(cs){
  if(!cs)return'';
  return cs.trim().toUpperCase().replace(/-(0+)(\d)/,'-$2');
}
function stripTime(detail){
  if(!detail)return'';
  return detail.replace(/\s*@\s*\d{1,2}:?\d{2}\s*$/,'').trim();
}
function ldot(c){return'<span class="ldot" style="background:'+c+'"></span>';}
function chip(t,c,b){return'<span class="chip" style="color:'+c+';background:'+b+'">'+t+'</span>';}
function noData(m){return'<div class="no-data">'+(m||'None')+'</div>';}

function eqCard(label,cls,status,who){
  return'<div class="eq-card '+cls+'">'+
    '<div class="eq-id">'+label+'</div>'+
    '<div class="eq-st">'+status+'</div>'+
    (who?'<div class="eq-who">'+who+'</div>':'')+
  '</div>';
}
function taskCard(t){
  var detail=stripTime(t.detail);
  return'<div class="task-item">'+
    '<div class="task-name">'+t.task+'</div>'+
    '<div class="oc-cards">'+
      '<div class="oc-sub">'+
        (detail?'<span class="oc-sub-val" style="color:var(--dim)">'+detail+'</span>':'')+
        (t.callSign?'<span class="oc-sub-val" style="color:#94b8d8">'+t.callSign+'</span>':'')+
      '</div>'+
    '</div>'+
  '</div>';
}
function lrow(l,r){
  return'<div class="list-row">'+l+'<span class="lv">'+r+'</span></div>';
}
function setM(id,val,cls){
  var el=document.getElementById(id);if(!el)return;
  el.textContent=val;el.className='stat-val'+(cls?' '+cls:'');
}
function findCurrentAssignment(posts){
  if(!posts||!posts.length)return null;
  for(var i=0;i<posts.length;i++){
    var p=posts[i];
    if(isActive(p.from,p.to))return p;
  }
  return null;
}

function render(){
  var personnel    = ls('fom_personnel',[]);
  var deskOfficer  = raw('fom_deskOfficer');
  var currentDate  = raw('fom_currentDate');
  var shift        = currentShift();
  var weather      = ls('fom_weather',{});
  var secKeys      = ls('fom_securityKeys',[]);
  var supRadios    = ls('fom_supervisorRadios',[]);
  var offRadios    = ls('fom_officerRadios',[]);
  var otherRadios  = ls('fom_otherRadios',[]);
  var recorders    = ls('fom_recorders',[]);
  var tasks        = ls('fom_secondaryTasks',[]);
  var oosState     = ls('fom_oos_state',{});
  var oosIds       = (oosState&&oosState.badges)||[];
  var otherKeys    = ls('fom_otherKeys',[]);
  var accessBadges = ls('fom_accessBadges',[]);
  var satPhones    = ls('fom_satPhones',[]);

  var active    = personnel.filter(function(p){return p.name;});
  var allRadios = supRadios.concat(offRadios).concat(otherRadios);

  var activeNames={};
  active.forEach(function(p){if(p.name)activeNames[p.name.trim().toLowerCase()]=true;});
  function isOnDuty(a){
    if(!a)return false;
    var al=a.trim().toLowerCase();
    if(activeNames[al])return true;
    var parts=al.split(/[,\s]+/);
    for(var i=0;i<parts.length;i++)
      for(var k in activeNames)
        if(k.indexOf(parts[i])>=0&&parts[i].length>2)return true;
    return false;
  }

  var onBreak   = active.filter(function(p){return p.posts&&p.posts.some(function(x){return x.isBreak;});}).length;
  var onPost    = active.filter(function(p){return p.posts&&p.posts.some(function(x){return x.post&&!x.isBreak;});}).length;
  var radiosOut = allRadios.filter(function(r){return r.assignedTo&&!r.oos&&isOnDuty(r.assignedTo);}).length;
  var radiosOOS = allRadios.filter(function(r){return r.oos;}).length;
  var keysOut   = secKeys.filter(function(k){return k.assignedTo&&!k.oos;}).length;
  var keysOOS   = secKeys.filter(function(k){return k.oos;}).length;
  var keysAvail = secKeys.filter(function(k){return!k.assignedTo&&!k.oos;}).length;
  var recsOut   = recorders.filter(function(r){return r.assignedTo&&!r.oos;}).length;
  var recsOOS   = recorders.filter(function(r){return r.oos;}).length;
  var badgesOOS = oosIds.length;
  var totalOOS  = keysOOS+radiosOOS+recsOOS+badgesOOS;

  var supNames = active.filter(function(p){return SUP_ROLES.indexOf(p.role)>=0&&p.role!=='OIC';})
    .map(function(p){return sn(p.name);}).join(' / ')||'\u2014';

  var dateStr = currentDate?(function(){
    try{return new Date(currentDate+'T12:00:00').toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric'});}
    catch(e){return currentDate;}
  }()):'';
  document.getElementById('tb-date').textContent = dateStr || '';
  document.getElementById('tb-weather').textContent=weather.temp?(weather.temp+(weather.conditions?' \u00b7 '+weather.conditions:'')):'';

  setM('m-duty',  active.length, active.length>0?'ok':'');
  setM('m-post',  onPost,'');
  // Incidents placeholder — wired to the Incident Reports system once built
  // (will read incidents for the current shift). Shows 0 until then.
  setM('m-incidents', 0, '');
  setM('m-rout',  radiosOut,'');
  setM('m-kout',  keysOut,'');
  setM('m-recout',recsOut,'');
  setM('m-boos',  badgesOOS, badgesOOS>0?'alert':'');
  setM('m-toos',  totalOOS,  totalOOS>0?'alert':active.length>0?'ok':'');

  document.getElementById('ib-sup').textContent  =supNames;
  document.getElementById('ib-desk').textContent =deskOfficer||'\u2014';
  document.getElementById('ib-stime').textContent=shiftTime(shift);
  document.getElementById('ib-shift').textContent=shift||'\u2014';
  document.getElementById('ib-duty').textContent =active.length;

  var RANK_MAP = {
    'Director of Security':'Dir','Assistant Director':'AD','Director':'Dir',
    'Lieutenant':'Lt','Dir/AD/Lt':'Dir','Sergeant':'Sgt','OIC':'OIC','Developer':'Dev','Officer':'Ofc'
  };
  document.getElementById('roster-sub').textContent='('+active.length+' active)';
  var rhtml='';
  active.forEach(function(p){
    var isSup    = SUP_ROLES.indexOf(p.role)>=0;
    var rank     = RANK_MAP[p.role]||p.role||'';
    var nameCls  = isSup?'oc-name sup':'oc-name';
    var allItems = (p.posts||[]).slice();
    allItems.sort(function(a,b){return postSortKey(a)-postSortKey(b);});
    var currentItem = findCurrentAssignment(allItems);
    var cardCls;
    if(isSup){
      cardCls = 'is-sup';
    } else if(currentItem && currentItem.isBreak){
      cardCls = 'post-break';
    } else if(currentItem && currentItem.post && POST_CLASS[currentItem.post]){
      cardCls = POST_CLASS[currentItem.post];
    } else {
      cardCls = 'idle';
    }
    var subCards = [];
    allItems.forEach(function(item){
      var isNow = (item === currentItem);
      var nowCls = isNow ? ' is-now' : '';
      if(item.isBreak){
        var breakLabel = 'Break';
        if(item.from && item.to) breakLabel = 'Break ' + item.from + '\u2013' + item.to;
        else if(item.from) breakLabel = 'Break @ ' + item.from;
        subCards.push('<div class="oc-sub'+nowCls+'"><span class="oc-sub-val break">'+breakLabel+'</span></div>');
      } else if(item.post){
        var label = item.post;
        if(item.from && item.to) label += ' ' + item.from + '\u2013' + item.to;
        subCards.push('<div class="oc-sub'+nowCls+'"><span class="oc-sub-val post">'+label+'</span></div>');
      }
    });
    if(p.radio)    subCards.push('<div class="oc-sub"><span class="oc-sub-val">Radio: '+p.radio+'</span></div>');
    if(p.key)      subCards.push('<div class="oc-sub"><span class="oc-sub-val">Key: '+p.key+'</span></div>');
    if(p.recorder) subCards.push('<div class="oc-sub"><span class="oc-sub-val">Rec: '+p.recorder+'</span></div>');
    if(p.badge)    subCards.push('<div class="oc-sub"><span class="oc-sub-val">Badge: '+p.badge+'</span></div>');
    rhtml+=
      '<div class="officer-card '+cardCls+'">'+
        '<div class="oc-name-row">'+
          (rank?'<span class="oc-rank">'+rank+'</span>':'')+
          '<span class="'+nameCls+'">'+sn(p.name)+'</span>'+
        '</div>'+
        (subCards.length?'<div class="oc-cards">'+subCards.join('')+'</div>':'')+
      '</div>';
  });
  document.getElementById('roster-wrap').innerHTML=rhtml||
    '<div class="no-data">No officers on duty</div>';

  var byPost={};
  PRIMARY_POSTS.forEach(function(p){byPost[p]=[];});
  active.forEach(function(p){
    (p.posts||[]).forEach(function(pp){
      if(pp.post&&!pp.isBreak&&byPost[pp.post]!==undefined&&isActive(pp.from,pp.to))
        byPost[pp.post].push(sn(p.name));
    });
  });
  var activePosts=PRIMARY_POSTS.filter(function(p){return byPost[p].length>0;});
  document.getElementById('primary-task-area').innerHTML=activePosts.length
    ?'<div class="task-list">'+activePosts.map(function(pn){
        var col=POST_COLORS[pn];
        var names=byPost[pn];
        return'<div class="task-item active" style="border-left-color:'+col+';background:rgba(0,0,0,0.2)">'+
          '<div class="task-name" style="color:var(--text)">'+pn+'</div>'+
          '<div class="oc-cards">'+names.map(function(n){
            return'<div class="oc-sub"><span class="oc-sub-val">'+n+'</span></div>';
          }).join('')+'</div>'+
        '</div>';
      }).join('')+'</div>'
    :'<div class="no-data">No active assignments</div>';

  var activeCS={};
  active.forEach(function(p){
    var cs=normCS(p.callSign);if(!cs)return;
    (p.posts||[]).forEach(function(pp){
      if(pp.post&&!pp.isBreak&&PRIMARY_POSTS.indexOf(pp.post)>=0&&isActive(pp.from,pp.to))
        activeCS[cs]=true;
    });
  });
  var activeTasks=tasks.filter(function(t){
    return t.callSign&&t.callSign.trim()&&activeCS[normCS(t.callSign)]===true;
  });
  document.getElementById('sec-task-sub').textContent=activeTasks.length?'('+activeTasks.length+')':'';
  document.getElementById('tasks-list').innerHTML=activeTasks.length
    ?activeTasks.map(taskCard).join('')
    :'<div class="no-data">No active tasks</div>';

  var SUP_OTHER_IDS=['95','97','99'];
  var supOtherRadios=otherRadios.filter(function(r){return SUP_OTHER_IDS.indexOf(String(r.id))>=0;});
  var remainingOtherRadios=otherRadios.filter(function(r){return SUP_OTHER_IDS.indexOf(String(r.id))<0;});
  var allSupRadios=supRadios.concat(supOtherRadios);

  function radioCard(r){
    var oos=!!r.oos,assigned=!!r.assignedTo&&!r.oos,onDuty=assigned&&isOnDuty(r.assignedTo);
    var cls=oos?'oos':(assigned&&onDuty)?'out':'avail';
    var status=oos?'OOS':(assigned&&onDuty)?'Out':'Avail';
    return eqCard(r.id,cls,status,(assigned&&onDuty)?sn(r.assignedTo):'');
  }

  function renderEqGrid(id,cards){
    var el=document.getElementById(id);if(!el)return;
    if(!cards.length){el.innerHTML=noData();el.style.gridTemplateColumns='';return;}
    var n=cards.length,cols=n<=6?n:Math.ceil(n/2);
    el.style.gridTemplateColumns='repeat('+cols+',1fr)';
    el.innerHTML=cards.join('');
  }

  var supOut=allSupRadios.filter(function(r){return r.assignedTo&&!r.oos&&isOnDuty(r.assignedTo);}).length;
  var supAvail=allSupRadios.filter(function(r){return(!r.assignedTo||!isOnDuty(r.assignedTo))&&!r.oos;}).length;
  var supOOS=allSupRadios.filter(function(r){return r.oos;}).length;
  document.getElementById('sup-radio-sub').textContent=supOut+' out \u00b7 '+supAvail+' avail \u00b7 '+supOOS+' OOS';
  renderEqGrid('sup-radio-grid',allSupRadios.map(radioCard));

  var offOut=offRadios.filter(function(r){return r.assignedTo&&!r.oos&&isOnDuty(r.assignedTo);}).length;
  var offAvail=offRadios.filter(function(r){return(!r.assignedTo||!isOnDuty(r.assignedTo))&&!r.oos;}).length;
  var offOOS=offRadios.filter(function(r){return r.oos;}).length;
  document.getElementById('off-radio-sub').textContent=offOut+' out \u00b7 '+offAvail+' avail \u00b7 '+offOOS+' OOS';
  renderEqGrid('off-radio-grid',offRadios.map(radioCard));

  document.getElementById('key-sub').textContent=keysOut+' out \u00b7 '+keysAvail+' avail \u00b7 '+keysOOS+' OOS';
  renderEqGrid('key-grid',secKeys.map(function(k){
    var oos=!!k.oos,assigned=!!k.assignedTo&&!k.oos;
    var cls=oos?'oos':assigned?'out':'avail';
    var status=oos?'OOS':assigned?'Out':'Avail';
    return eqCard(k.number,cls,status,assigned?sn(k.assignedTo):'');
  }));

  document.getElementById('rec-sub').textContent=recsOut+' out \u00b7 '+recsOOS+' OOS';
  renderEqGrid('rec-grid',recorders.map(function(r){
    var oos=!!r.oos,out=!!r.assignedTo&&!r.oos;
    return eqCard('#'+r.id,oos?'oos':out?'out':'avail',oos?'OOS':out?'Out':'Avail',out?sn(r.assignedTo):'');
  }));

  var otherOut=remainingOtherRadios.filter(function(r){return r.assignedTo&&!r.oos&&isOnDuty(r.assignedTo);}).length;
  document.getElementById('other-radio-sub').textContent=otherOut+' assigned';
  renderEqGrid('other-radio-grid',remainingOtherRadios.map(radioCard));

  var okeys =otherKeys.filter(function(k){return!k.available&&k.assignedTo;}).length;
  var obadge=accessBadges.filter(function(a){return!a.available&&!a.oos;}).length;
  var osat  =satPhones.filter(function(s){return!s.available&&!s.oos;}).length;
  document.getElementById('other-items-list').innerHTML=
    lrow(ldot('var(--blue)')+'<span class="lf">Other keys out</span>',okeys)+
    lrow(ldot('var(--blue)')+'<span class="lf">Access badges out</span>',obadge)+
    lrow(ldot('var(--blue)')+'<span class="lf">Sat phones out</span>',osat);

  var oosBar=document.getElementById('oos-bar');
  if(totalOOS>0){
    oosBar.style.display='flex';
    document.getElementById('oos-title').textContent='\u26a0 Out of Service \u2014 '+totalOOS+' item'+(totalOOS!==1?'s':'');
    var chips=[];
    secKeys.filter(function(k){return k.oos;}).forEach(function(k){chips.push(chip('Key '+k.number,'#c0392b','rgba(192,57,43,0.15)'));});
    allRadios.filter(function(r){return r.oos;}).forEach(function(r){chips.push(chip('Radio '+r.id,'#c0392b','rgba(192,57,43,0.15)'));});
    recorders.filter(function(r){return r.oos;}).forEach(function(r){chips.push(chip('Rec '+r.id,'#c0392b','rgba(192,57,43,0.15)'));});
    oosIds.forEach(function(id){chips.push(chip('Badge '+id,'#c0392b','rgba(192,57,43,0.15)'));});
    document.getElementById('oos-chips').innerHTML=chips.join('');
  }else{
    oosBar.style.display='none';
  }

  document.getElementById('last-updated').textContent='Updated '+new Date().toLocaleTimeString();
}

function wireScrollFeedback(){
  var els = document.querySelectorAll('.scrollable');
  els.forEach(function(el){
    var t;
    el.addEventListener('scroll', function(){
      el.classList.add('is-scrolling');
      clearTimeout(t);
      t = setTimeout(function(){ el.classList.remove('is-scrolling'); }, 800);
    });
  });
}

setInterval(function(){document.getElementById('tb-clock').textContent=nowET();},1000);
document.getElementById('tb-clock').textContent=nowET();
render();
wireScrollFeedback();
setInterval(render, 30000);
window.addEventListener('fom-cloud-updated', render);
window.addEventListener('castellan:pull-complete', render);
window.addEventListener('storage',function(e){ if(e.key&&e.key.indexOf('fom_')===0) render(); });
`

export default function DashboardPage() {
  const containerRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  useEffect(() => {
    const styleEl = document.createElement('style')
    styleEl.id = 'dashboard-page-styles'
    styleEl.textContent = CSS
    document.head.appendChild(styleEl)

    const container = containerRef.current
    if (!container) return
    container.innerHTML = HTML

    const backBtn = container.querySelector('#tb-back') as HTMLAnchorElement | null
    if (backBtn) {
      backBtn.removeAttribute('href')
      backBtn.style.cursor = 'pointer'
      backBtn.onclick = (e) => { e.preventDefault(); navigate(-1 as any) }
    }

    const scriptEl = document.createElement('script')
    scriptEl.id = 'dashboard-page-script'
    // Absolute paths break under file:// (Electron). Use relative when running as a local file.
    scriptEl.src = window.location.protocol === 'file:' ? 'dashboard-logic.js' : '/dashboard-logic.js'
    container.appendChild(scriptEl)

    const refreshBtn = container.querySelector('#tb-refresh') as HTMLButtonElement | null
    if (refreshBtn) {
      refreshBtn.removeAttribute('onclick')
      refreshBtn.addEventListener('click', () => {
        if (typeof (window as any).render === 'function') (window as any).render()
      })
    }

    return () => {
      styleEl.remove()
      scriptEl.remove()
    }
  }, [navigate])

  return (
    <div
      ref={containerRef}
      id="dashboard-page-root"
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100vw',
        height: '100dvh',
        background: 'var(--bg-deep, #060d18)',
        overflow: 'hidden',
        zIndex: 900,
      }}
    />
  )
}
